import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Check, X, Sparkles, Star, Users, Heart, Crown, Volume2, Globe, HelpCircle, UserPlus } from "lucide-react";

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: delay * 0.001, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

const TIERS = [
  { key: "free", name: "Free", monthly: "$0", annual: "$0", biannual: "$0", icon: Sparkles, accent: "#6E7385", desc: "Explore the basics" },
  { key: "plus", name: "Omni+", monthly: "$9.99", annual: "$99.99", biannual: "$59.94", icon: Star, accent: "#F5A623", desc: "For the individual", popular: true },
  { key: "duo", name: "Duo", monthly: "$18", annual: "$194.40", biannual: "$108", icon: Users, accent: "#34D399", desc: "For two" },
  { key: "family", name: "Family", monthly: "$24.99", annual: "$269.89", biannual: "$149.94", icon: Heart, accent: "#A78BFA", desc: "For the family" },
  { key: "platinum", name: "Group", monthly: "$34.99", biannual: "$209.94", annual: "$377.89", icon: Crown, accent: "#E31212", desc: "Group subscription", biannualOnly: true },
];

const FEATURES = [
  { name: "Messages per day", free: "5", plus: "50", duo: "Unlimited", family: "Unlimited", platinum: "Unlimited" },
  { name: "AI Modules", free: "1", plus: "All 6", duo: "All 6", family: "All 6", platinum: "All 6" },
  { name: "Wisdom Council", free: "Preview", plus: "Full", duo: "Full", family: "Full", platinum: "Full" },
  { name: "Mood Tracking", free: "Basic", plus: "Advanced", duo: "Advanced", family: "Family", platinum: "Team-wide" },
  { name: "Wisdom Council", free: false, plus: true, duo: true, family: true, platinum: true },
  { name: "AI Memory", free: "7 days", plus: "Unlimited", duo: "Unlimited", family: "Unlimited", platinum: "Unlimited" },
  { name: "Voice (TTS)", free: "5/mo", plus: "20/mo", duo: "40/mo", family: "80/mo", platinum: "Unlimited" },
  { name: "Speech Input (STT)", free: true, plus: true, duo: true, family: true, platinum: true },
  { name: "Goals & Streaks", free: false, plus: true, duo: true, family: true, platinum: true },
  { name: "Smart Backup", free: false, plus: true, duo: true, family: true, platinum: true },
  { name: "Members", free: "1", plus: "1", duo: "2", family: "4", platinum: "6 (base)" },
  { name: "Extra Members", free: false, plus: false, duo: false, family: false, platinum: "+$7.99/mo each" },
  { name: "Billing Minimum", free: "—", plus: "Monthly", duo: "Monthly", family: "Monthly", platinum: "Bi-annual (6mo)" },
  { name: "Admin Dashboard", free: false, plus: false, duo: false, family: false, platinum: true },
  { name: "Member Privacy Levels", free: false, plus: false, duo: false, family: false, platinum: true },
  { name: "Priority Support", free: false, plus: false, duo: false, family: false, platinum: true },
  { name: "Biometric Login", free: false, plus: true, duo: true, family: true, platinum: true },
  { name: "Cultural Awareness", free: "Standard", plus: "Ultra", duo: "Ultra", family: "Ultra", platinum: "Ultra" },
];

const ADDONS = [
  { name: "Extra Group Member", price: "$7.99/mo", desc: "Add unlimited members to your Group plan — activated by admin, billed bi-annually with subscription", icon: UserPlus },
  { name: "Voice Unlimited", price: "$9.99/mo", desc: "Unlimited text-to-speech across all modules", icon: Volume2 },
  { name: "Global Voice Pack", price: "$4.99/mo", desc: "TTS in 107+ premium languages for Wisdom Council", icon: Globe },
];

const FAQ = [
  { q: "Can I switch plans anytime?", a: "Yes. Upgrade or downgrade at any time. Changes take effect immediately, and billing is pro-rated." },
  { q: "Is there a free trial for paid plans?", a: "The Free tier is available forever with no credit card required. You can explore Omni's core features before deciding to upgrade." },
  { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards through Stripe. Apple Pay and Google Pay are also supported." },
  { q: "How does the Group plan billing work?", a: "The Group plan is billed bi-annually (every 6 months) as a single group payment. The admin pays for all members in one invoice — $209.94/6mo base for 6 members, plus $47.94/6mo per additional member." },
  { q: "Can I add more than 6 members to the Group plan?", a: "Yes. The group admin can add unlimited extra members at $7.99/mo each (billed bi-annually at $47.94/6mo per member). All add-ons are managed from the Group Admin dashboard." },
  { q: "How does privacy work in the Group plan?", a: "Each member gets individual privacy controls based on their age. Adults (18+) have full privacy — the admin cannot view their conversations or personal data. Teens (13-17) have moderate privacy with activity summaries visible to the admin. Children under 13 are fully supervised." },
  { q: "What happens to my data if I downgrade?", a: "Your data is always yours. If you downgrade, you retain read access to all existing data. Active features adjust to your new plan." },
  { q: "Is Omni available on mobile?", a: "Yes. Omni works as a Progressive Web App (PWA) on any device, and native iOS/Android apps are available." },
];

function CellValue({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-emerald-500 mx-auto" />;
  if (value === false) return <X className="w-4 h-4 text-[#6E7385]/40 mx-auto" />;
  return <span className="text-sm text-[#A1A5B3]">{value}</span>;
}

export default function PricingPage() {
  const [billing, setBilling] = useState("monthly");

  return (
    <>
      {/* ═══════ HERO ═══════ */}
      <section className="relative pt-32 pb-10 md:pt-44 md:pb-16 px-6 lg:px-12" data-testid="pricing-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px]" style={{ background: "radial-gradient(ellipse, rgba(245,166,35,0.08) 0%, transparent 65%)" }} />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-6">Pricing</p>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="text-5xl sm:text-6xl font-semibold tracking-tighter leading-[1.05] mb-6"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Simple, transparent pricing
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg text-[#A1A5B3] max-w-xl mx-auto mb-10 leading-relaxed">
              Every plan includes encrypted storage, cultural awareness, and 24/7 availability. Start free and upgrade when ready.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="inline-flex items-center rounded-full p-1 border" style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.03)" }}>
              <button data-testid="pricing-billing-monthly" onClick={() => setBilling("monthly")}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${billing === "monthly" ? "text-white" : "text-[#6E7385]"}`}
                style={billing === "monthly" ? { background: "#E31212" } : {}}>
                Monthly
              </button>
              <button data-testid="pricing-billing-annual" onClick={() => setBilling("annual")}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${billing === "annual" ? "text-white" : "text-[#6E7385]"}`}
                style={billing === "annual" ? { background: "#E31212" } : {}}>
                Annual <span className="text-[10px] ml-1 opacity-70">Save 17%</span>
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ PRICING CARDS ═══════ */}
      <section className="py-12 px-6 lg:px-12" data-testid="pricing-cards">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {TIERS.map((tier, i) => {
            const Icon = tier.icon;
            const price = tier.biannualOnly && billing === "monthly"
              ? tier.biannual
              : billing === "monthly" ? tier.monthly : tier.annual;
            const period = tier.biannualOnly && billing === "monthly" ? "/6mo" : (tier.key !== "free" ? (billing === "monthly" ? "/mo" : "/yr") : "");
            return (
              <Reveal key={tier.key} delay={i * 60}>
                <div className="relative rounded-2xl p-6 border h-full flex flex-col"
                  style={{
                    background: "#0A0E1A",
                    borderColor: tier.popular ? "rgba(245,166,35,0.4)" : "rgba(255,255,255,0.07)",
                    boxShadow: tier.popular ? "0 0 30px rgba(245,166,35,0.08)" : "none",
                  }}>
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                      style={{ background: "#F5A623", color: "#060912" }}>
                      Most Popular
                    </div>
                  )}
                  {tier.biannualOnly && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                      style={{ background: "#E31212", color: "#fff" }}>
                      Bi-Annual
                    </div>
                  )}
                  <Icon className="w-5 h-5 mb-4" style={{ color: tier.accent }} strokeWidth={1.5} />
                  <h3 className="text-lg font-medium text-white mb-1">{tier.name}</h3>
                  <p className="text-xs text-[#6E7385] mb-5">{tier.desc}</p>
                  <div className="mb-4">
                    <span className="text-4xl font-semibold text-white tracking-tight">{price}</span>
                    <span className="text-sm text-[#6E7385]">{period}</span>
                  </div>
                  {tier.biannualOnly && (
                    <p className="text-[10px] text-[#E31212] mb-4 font-medium">6 members base · +$7.99/mo per extra member</p>
                  )}
                  <Link to="/auth" className="mt-auto">
                    <button className="w-full py-3 rounded-full text-sm font-medium transition-all duration-300 hover:brightness-110"
                      style={{
                        background: tier.popular ? "#F5A623" : "rgba(255,255,255,0.05)",
                        color: tier.popular ? "#060912" : "#fff",
                        border: tier.popular ? "none" : "1px solid rgba(255,255,255,0.1)",
                      }}>
                      {tier.key === "free" ? "Get Started" : "Choose Plan"}
                    </button>
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ═══════ FEATURE COMPARISON TABLE ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" data-testid="pricing-comparison">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-12 text-center"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Compare every feature
            </h2>
          </Reveal>

          <Reveal delay={100}>
            <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr style={{ background: "#0A0E1A" }}>
                    <th className="text-left px-5 py-4 text-sm font-medium text-[#6E7385]">Feature</th>
                    {TIERS.map(t => (
                      <th key={t.key} className="px-4 py-4 text-center">
                        <span className="text-sm font-medium" style={{ color: t.accent }}>{t.name}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FEATURES.map((f, i) => (
                    <tr key={f.name} style={{ background: i % 2 === 0 ? "rgba(255,255,255,0.01)" : "transparent", borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                      <td className="px-5 py-3.5 text-sm text-[#A1A5B3]">{f.name}</td>
                      {["free", "plus", "duo", "family", "platinum"].map(k => (
                        <td key={k} className="px-4 py-3.5 text-center"><CellValue value={f[k]} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ ADD-ONS ═══════ */}
      <section className="py-16 px-6 lg:px-12" style={{ background: "#0A0E1A" }}>
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F5A623] mb-4">Add-ons</p>
            <h2 className="text-3xl font-semibold tracking-tight mb-10"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Enhance any plan
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ADDONS.map((a, i) => (
              <Reveal key={a.name} delay={i * 60} className="rounded-2xl p-6 border flex items-start gap-5"
                style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(245,166,35,0.12)" }}>
                <a.icon className="w-6 h-6 text-[#F5A623] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <h3 className="text-base font-medium text-white mb-1">{a.name}</h3>
                  <p className="text-xs text-[#6E7385] mb-2">{a.desc}</p>
                  <p className="text-sm font-semibold text-[#F5A623]">{a.price}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ FAQ ═══════ */}
      <section className="py-28 md:py-36 px-6 lg:px-12" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="flex items-center gap-3 mb-10">
              <HelpCircle className="w-5 h-5 text-[#E31212]" strokeWidth={1.5} />
              <h2 className="text-3xl font-semibold tracking-tight"
                style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
                Frequently asked questions
              </h2>
            </div>
          </Reveal>
          <div className="space-y-0">
            {FAQ.map((item, i) => (
              <Reveal key={i} delay={i * 40}>
                <details className="group border-b py-5" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <span className="text-base text-white font-medium pr-4">{item.q}</span>
                    <span className="text-[#6E7385] text-xl group-open:rotate-45 transition-transform duration-200">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-[#A1A5B3] leading-relaxed">{item.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight mb-6"
              style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
              Start for free today
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-lg text-[#A1A5B3] mb-10">No credit card required. Upgrade when you're ready.</p>
          </Reveal>
          <Reveal delay={200}>
            <Link to="/auth" data-testid="pricing-cta-btn">
              <button className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-white font-medium text-lg transition-all duration-300 hover:-translate-y-1"
                style={{ background: "#E31212", boxShadow: "0 0 40px rgba(227,18,18,0.35)" }}>
                Get Omni Free <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
